/* eslint-disable max-lines, react/no-array-index-key */
import React from 'react';

/**
 * Minimal, dependency-free Markdown renderer for assistant chat bubbles.
 *
 * Supports:
 *   - ATX headings (#, ##, ###, ####)
 *   - Fenced code blocks (``` with optional language)
 *   - Unordered (-, *, +) and ordered (1.) lists
 *   - Blockquotes (>)
 *   - Inline: **bold**, *italic*, `code`, [text](url), autolinks
 *   - Paragraphs separated by blank lines
 *
 * All raw text is HTML-escaped before any markdown transforms run, so the
 * output is safe to drop into the DOM as React elements. We never use
 * dangerouslySetInnerHTML.
 */

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });

const INLINE_CODE = /`([^`]+)`/g;
const BOLD = /\*\*([^*]+)\*\*/g;
const ITALIC = /(^|[^*])\*([^*\n]+)\*/g;
const LINK = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const AUTOLINK = /(^|\s)(https?:\/\/[^\s<]+)/g;

interface ITokenSpan {
  kind: 'text' | 'html';
  value: string;
}

const decodeHtml = (s: string): string =>
  s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

const renderInline = (raw: string, keyPrefix: string): React.ReactNode[] => {
  // Run transforms on the escaped string, producing safe HTML, then
  // split it back into React nodes so we don't need dangerouslySetInnerHTML
  // on the final output. We rebuild via a tiny placeholder protocol.
  let s = escapeHtml(raw);

  const tokens: ITokenSpan[] = [];
  const push = (html: string): string => {
    tokens.push({ kind: 'html', value: html });
    return `\u0000${tokens.length - 1}\u0000`;
  };

  s = s.replace(INLINE_CODE, (_m, code) => push(`<code>${code}</code>`));
  s = s.replace(LINK, (_m, text, href) =>
    push(`<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`),
  );
  s = s.replace(AUTOLINK, (_m, pre, href) =>
    `${pre}${push(`<a href="${href}" target="_blank" rel="noopener noreferrer">${href}</a>`)}`,
  );
  s = s.replace(BOLD, (_m, inner) => push(`<strong>${inner}</strong>`));
  s = s.replace(ITALIC, (_m, pre, inner) => `${pre}${push(`<em>${inner}</em>`)}`);

  // Now split on placeholder markers and rebuild as React nodes.
  const parts = s.split(/\u0000(\d+)\u0000/);
  return parts.map((part, idx) => {
    if (idx % 2 === 1) {
      const tok = tokens[Number(part)];
      // Render the small set of HTML tags we produced ourselves.
      const html = tok?.value || '';
      const codeMatch = html.match(/^<code>(.+)<\/code>$/s);
      if (codeMatch) {
        return (
          <code key={`${keyPrefix}-${idx}`} className="md-inline-code">
            {decodeHtml(codeMatch[1])}
          </code>
        );
      }
      const strongMatch = html.match(/^<strong>(.+)<\/strong>$/s);
      if (strongMatch) {
        return <strong key={`${keyPrefix}-${idx}`}>{decodeHtml(strongMatch[1])}</strong>;
      }
      const emMatch = html.match(/^<em>(.+)<\/em>$/s);
      if (emMatch) {
        return <em key={`${keyPrefix}-${idx}`}>{decodeHtml(emMatch[1])}</em>;
      }
      const linkMatch = html.match(/^<a href="([^"]+)"[^>]*>(.+)<\/a>$/s);
      if (linkMatch) {
        return (
          <a
            key={`${keyPrefix}-${idx}`}
            href={linkMatch[1]}
            target="_blank"
            rel="noopener noreferrer"
          >
            {decodeHtml(linkMatch[2])}
          </a>
        );
      }
      return null;
    }
    if (!part) return null;
    return <React.Fragment key={`${keyPrefix}-${idx}`}>{decodeHtml(part)}</React.Fragment>;
  });
};

interface IBlock {
  kind: 'heading' | 'paragraph' | 'code' | 'ul' | 'ol' | 'quote';
  level?: number;
  language?: string;
  lines: string[];
}

const tokenizeBlocks = (md: string): IBlock[] => {
  const blocks: IBlock[] = [];
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    const fence = line.match(/^```(\w+)?\s*$/);
    if (fence) {
      const language = fence[1];
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // consume closing fence (or EOF)
      blocks.push({ kind: 'code', language, lines: body });
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,4})\s+(.+?)\s*#*\s*$/);
    if (heading) {
      blocks.push({ kind: 'heading', level: heading[1].length, lines: [heading[2]] });
      i += 1;
      continue;
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const body: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        body.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push({ kind: 'quote', lines: body });
      continue;
    }

    // Unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ''));
        i += 1;
      }
      blocks.push({ kind: 'ul', lines: items });
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push({ kind: 'ol', lines: items });
      continue;
    }

    // Blank line
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Paragraph (consume until blank line or block start)
    const para: string[] = [line];
    i += 1;
    while (i < lines.length) {
      const next = lines[i];
      if (
        next.trim() === '' ||
        /^```/.test(next) ||
        /^#{1,4}\s+/.test(next) ||
        /^\s*>\s?/.test(next) ||
        /^\s*[-*+]\s+/.test(next) ||
        /^\s*\d+\.\s+/.test(next)
      ) {
        break;
      }
      para.push(next);
      i += 1;
    }
    blocks.push({ kind: 'paragraph', lines: para });
  }
  return blocks;
};

export const renderMarkdown = (markdown: string): React.ReactNode => {
  if (!markdown) return null;
  const blocks = tokenizeBlocks(markdown);
  return (
    <div className="md-root">
      {blocks.map((b, idx) => {
        const key = `b-${idx}`;
        if (b.kind === 'code') {
          return (
            <pre key={key} className="md-code-block">
              <code data-lang={b.language || ''}>{b.lines.join('\n')}</code>
            </pre>
          );
        }
        if (b.kind === 'heading') {
          const level = Math.min(Math.max(b.level || 1, 1), 4);
          const text = renderInline(b.lines[0] || '', key);
          if (level === 1) return <h1 key={key} className="md-h1">{text}</h1>;
          if (level === 2) return <h2 key={key} className="md-h2">{text}</h2>;
          if (level === 3) return <h3 key={key} className="md-h3">{text}</h3>;
          return <h4 key={key} className="md-h4">{text}</h4>;
        }
        if (b.kind === 'ul') {
          return (
            <ul key={key} className="md-ul">
              {b.lines.map((li, j) => (
                <li key={`${key}-${j}`}>{renderInline(li, `${key}-${j}`)}</li>
              ))}
            </ul>
          );
        }
        if (b.kind === 'ol') {
          return (
            <ol key={key} className="md-ol">
              {b.lines.map((li, j) => (
                <li key={`${key}-${j}`}>{renderInline(li, `${key}-${j}`)}</li>
              ))}
            </ol>
          );
        }
        if (b.kind === 'quote') {
          return (
            <blockquote key={key} className="md-quote">
              {b.lines.map((ln, j) => (
                <p key={`${key}-${j}`}>{renderInline(ln, `${key}-${j}`)}</p>
              ))}
            </blockquote>
          );
        }
        // paragraph — join soft-wrapped lines with spaces, preserve hard breaks via <br/>
        const text = b.lines.join('\n');
        const parts = text.split('\n');
        return (
          <p key={key} className="md-p">
            {parts.map((ln, j) => (
              <React.Fragment key={`${key}-${j}`}>
                {j > 0 && <br />}
                {renderInline(ln, `${key}-${j}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export default renderMarkdown;
